import FileFill from "~icons/eva/file-fill"
import PersonOutline from "~icons/eva/person-outline"
import HomeOutline from "~icons/eva/home-outline"
import CreditCardOutline from "~icons/eva/credit-card-outline"
import UploadFill from "~icons/eva/upload-fill"
import ShieldOutline from "~icons/eva/shield-outline"
import StarOutline from "~icons/eva/star-outline"
import styles from "./styles.module.scss"
import { DataGrid } from "./components/DataGrid"
import { SelectItem } from "./components/SelectItem"
import { observer } from "mobx-react-lite"
import { AnimatedTabs } from "../CruiseDetails/components/AnimatedTabs"
import { useBookingLogic } from "./useBookingLogic"
import { useRoomDetails } from "./useRoomDetails"
import { useStores } from "../../../models/helpers/useStores"
import { getSearchCriteriaHash } from "../../../models/helpers/getSearchCriteriaHash"
import { getCruiseDetails } from "../../../services/firestore/cruiseDetails"
import { useQuery } from "@tanstack/react-query"
import { FormSelectInput } from "../../atoms/FormSelectInput"
import { useForm } from "react-hook-form"
import { useEffect, useRef } from "react"
import { CabinGradeType, CardBrandNiceTypeMap, PaymentRequestStatusEnum } from "@cruisen/contract"
import { getAllCabinTypes, sortDecks } from "@cruisen/react-shared"
import { SkeletonTheme } from "react-loading-skeleton"
import { colors } from "@theme/colors"
import { DefaultInput } from "../../atoms/DefaultInput"
import { FormCurrencyInput } from "../../atoms/FormCurrencyInput"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import AlertCircleOutline from "~icons/eva/alert-circle-outline"
import { getPaymentRequests } from "../../../services/firebase-functions/billing"
import { formatCurrency } from "@cruisen/i18n"
import { currencyToNumber } from "./utils"
import classNames from "classnames"
import CloseModalButton from "../../buttons/CloseModalButton"
import { getMockedPaymentRequest } from "../../../utils/demoTripLogic"
import { usePaymentData } from "../../../hooks/usePaymentData"

const bookingSchema = yup.object().shape({
  cabinFare: yup.string().required("-"),
  taxesAndFees: yup.string().required("-"),
})

export const BookingModal = observer(
  ({ closeModal, initialRoomId }: { closeModal: () => void; initialRoomId: string }) => {
    const rootStore = useStores()
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const isDemoTrip = rootStore.activeTrip?.tags?.includes("demo")

    const { control, watch, setValue } = useForm({
      defaultValues: {
        cabinFare: "",
        taxesAndFees: "",
      },
      resolver: yupResolver(bookingSchema),
      mode: "all",
    })

    const [cabinFare, taxesAndFees] = watch(["cabinFare", "taxesAndFees"])

    const {
      store,
      isDragging,
      bookRoom,
      setConfirmationFile,
      isRoomBooking,
      confirmationFile,
      isPaymentMethodsLoading,
      revealSelectedPaymentMethod,
      isPaymentMethodRevealing,
    } = useBookingLogic({
      initialRoomId,
      closeModal,
      activeTrip: rootStore.activeTrip,
      loggedInUser: rootStore.loggedInUser,
    })

    const { tripRooms, roomData, tripRoomGuests, gridData, revealPassport } = useRoomDetails({
      selectedRoomId: store.currentRoomId || initialRoomId,
      setSelectedGuest: (guestId) => store.setSelectedGuestId(guestId),
      selectedGuestId: store.selectedGuestId,
      setSelectedPaymentMethod: (paymentMethod) => store.setSelectedPaymentMethod(paymentMethod),
      setFilteredPaymentMethods: (paymentMethods) =>
        store.setFilteredPaymentMethods(paymentMethods),
      paymentMethods: store.paymentMethods || [],
      activeTrip: rootStore.activeTrip,
      approvedPaymentRequest: store.approvedPaymentRequest,
      unmaskedPassportNumber: store.unmaskedPassportNumber,
      setUnmaskedPassportNumber: (unmaskedPassportNumber) =>
        store.setUnmaskedPassportNumber(unmaskedPassportNumber),
    })

    const { paymentData } = usePaymentData({
      isPaymentMethodsLoading,
      selectedPaymentMethod: store.selectedPaymentMethod,
      isPaymentMethodRevealing,
    })

    const cruiseDetailsQueryKey = [
      "cruiseDetails",
      rootStore.activeTrip.id,
      getSearchCriteriaHash(rootStore.activeTrip),
      rootStore.activeTrip.chosenTripSearchResultId,
    ]

    const handleAttachFile = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0]
        setConfirmationFile(selectedFile)
      }
    }

    const { data: cruiseDetailsData } = useQuery({
      queryKey: cruiseDetailsQueryKey,
      queryFn: () =>
        getCruiseDetails(
          rootStore.activeTrip.id,
          rootStore.activeTrip.chosenTripSearchResultId,
          "main",
        ),
      enabled: !!rootStore.activeTrip.chosenTripSearchResultId,
    })

    const decks = [...(cruiseDetailsData?.decks ?? [])]
    sortDecks(decks)

    const decksOptions = decks.map((deck) => ({ label: deck.name, value: String(deck.id) }))

    const { data: paymentRequests, isPending: isPaymentRequestsLoading } = useQuery({
      queryKey: ["paymentRequests", rootStore.activeTrip.id],
      queryFn: () => getPaymentRequests({ tripId: rootStore.activeTrip.id }),
      staleTime: 0,
    })

    const isSaveButtonDisabled = isDemoTrip
      ? false
      : !confirmationFile ||
        !cabinFare ||
        !taxesAndFees ||
        !store.confirmationNumber ||
        !store.cabinGrade

    useEffect(() => {
      if (isDemoTrip) {
        const mockedPaymentRequest = getMockedPaymentRequest({
          tripId: rootStore.activeTrip.id,
          roomId: store.currentRoomId,
          cruiseDetailsData: cruiseDetailsData,
        })

        const metaData = mockedPaymentRequest.paymentRequest.lineItems[0].meta

        setValue("cabinFare", mockedPaymentRequest.paymentRequest.lineItems[0]?.total || "", {
          shouldValidate: true,
          shouldDirty: true,
        })
        setValue(
          "taxesAndFees",
          mockedPaymentRequest.paymentRequest.lineItems[0]?.taxesAndFees || "",
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        )

        store.setCabinNo(metaData?.cabinNo || "")
        store.setCabinGrade((metaData?.cabinGradeType as CabinGradeType) || null)
        store.setDeck({ id: Number(metaData?.deckId), name: metaData?.deckName })
      }

      const paymentRequestList =
        paymentRequests?.filter(
          (paymentRequest) => paymentRequest.lineItems[0].tripRoomId === roomData.id,
        ) || []

      const approvedPaymentRequest = paymentRequestList.find(
        (paymentRequest) =>
          paymentRequest.status === PaymentRequestStatusEnum.Approved ||
          paymentRequest.status === PaymentRequestStatusEnum.PartiallyPaid ||
          paymentRequest.status === PaymentRequestStatusEnum.TotalPaid ||
          paymentRequest.status === PaymentRequestStatusEnum.Confirmed,
      )

      store.setApprovedPaymentRequest(approvedPaymentRequest || null)

      if (approvedPaymentRequest) {
        const metaData = approvedPaymentRequest.lineItems[0].meta

        const cabinFare = approvedPaymentRequest.lineItems[0]?.amount || ""

        setValue("cabinFare", cabinFare, {
          shouldValidate: true,
          shouldDirty: true,
        })
        const taxesAndFees = approvedPaymentRequest.lineItems[0]?.taxesAndFees || ""
        setValue("taxesAndFees", taxesAndFees, {
          shouldValidate: true,
          shouldDirty: true,
        })

        store.setCabinNo(metaData?.cabinNo || "")
        store.setCabinGrade((metaData?.cabinGradeType as CabinGradeType) || null)

        const foundDeck = decks.find((deck) => deck.id === Number(metaData?.deckId))
        if (foundDeck) {
          store.setDeck({ id: foundDeck.id, name: foundDeck.name })
        }
      }
    }, [roomData, cruiseDetailsData, paymentRequests])

    useEffect(() => {
      if (isDemoTrip) {
        store.setConfirmationNumber("1234567890")
      }
    }, [isDemoTrip])

    const allowBooking = isDemoTrip
      ? true
      : store.approvedPaymentRequest &&
        rootStore.activeTrip.chosenTripSearchResultId &&
        !roomData.bookingDetails

    const hasCruiseSailingDatePassed =
      cruiseDetailsData?.tripSearchResult?.startDate &&
      new Date(cruiseDetailsData?.tripSearchResult?.startDate).getTime() < new Date().getTime()

    const cabinTypes = getAllCabinTypes()
    const cabinTypesOptions = cabinTypes.map((cabinType) => ({
      label: cabinType.label,
      value: cabinType.id,
    }))

    const paymentMethodsToShow =
      roomData.bookingDetails && !store.approvedPaymentRequest
        ? store.paymentMethods.filter((paymentMethod) => {
            if (isDemoTrip) return true
            return roomData.guests.some((guest) => guest.userId === paymentMethod.userId)
          })
        : store.filteredPaymentMethods

    const hasPassportNumber = gridData.passportDetails.some(
      (detail) => detail.label === "Passport number" && detail.value,
    )

    return (
      <SkeletonTheme
        baseColor={colors.backgroundAdditional}
        highlightColor={colors.backgroundQuaternary}
      >
        <div className={styles.container}>
          {isDragging && (
            <div className={styles.overlay}>
              <h2>Drop file here to upload</h2>
            </div>
          )}
          <div className={styles.header}>
            <div>
              <h2>{allowBooking ? "Book room" : "Room details"}</h2>
              <AnimatedTabs
                chosenItem={roomData.id}
                items={tripRooms}
                onPress={(itemId) => {
                  store.setCurrentRoomId(itemId)
                }}
              />
            </div>
            <CloseModalButton onClick={closeModal} />
          </div>
          <div className={styles.scrollContainer} data-testid="room-booking-modal">
            {hasCruiseSailingDatePassed && allowBooking ? (
              <div className={styles.datePassedWarning}>
                <AlertCircleOutline className={styles.datePassedWarningIcon} />
                <p className={styles.datePassedWarningText}>
                  The cruise sailing date has passed. Please select a different cruise.
                </p>
              </div>
            ) : null}
            {tripRoomGuests.length > 0 ? (
              <>
                <h3 className={styles.sectionTitle}>Guests</h3>
                <div className={styles.section}>
                  <div className={styles.itemsList}>
                    {tripRoomGuests.map((guest) => (
                      <SelectItem
                        key={guest.guestId}
                        label={`${guest.firstName} ${guest.lastName}`}
                        isActive={guest.guestId === store.selectedGuestId}
                        onClick={() => store.setSelectedGuestId(guest.guestId)}
                        error={guest.isDataFilled || isDemoTrip ? "" : "Profile incomplete"}
                      />
                    ))}
                  </div>
                  <div className={styles.profileDetails}>
                    <DataGrid
                      icon={<PersonOutline className={styles.sectionIcon} />}
                      title="Personal info"
                      data={gridData.personalInfo}
                    />
                    <DataGrid
                      icon={<ShieldOutline className={styles.sectionIcon} />}
                      title="Passport details"
                      data={gridData.passportDetails}
                      onDataReveal={hasPassportNumber ? revealPassport : undefined}
                    />
                    <DataGrid
                      icon={<HomeOutline className={styles.sectionIcon} />}
                      title="Residence"
                      data={gridData.residence}
                    />
                    <DataGrid
                      icon={<StarOutline className={styles.sectionIcon} />}
                      title="Loyalty numbers"
                      data={gridData.cruiseLineLoyaltyMembers}
                      emptyStateText="No loyalty numbers added"
                    />
                  </div>
                </div>
              </>
            ) : null}
            {allowBooking || roomData.bookingDetails ? (
              <>
                <h3 className={styles.sectionTitle}>Payment methods</h3>
                <div className={styles.section}>
                  {!isPaymentMethodsLoading && paymentMethodsToShow.length === 0 ? (
                    <div className={styles.emptyStateContainer}>
                      <p className={styles.emptyStateTitle}>No payment methods added</p>
                      <p className={styles.emptyStateText}>
                        This room does not have any payment methods added. Your client can add one
                        when accepting a quote.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.itemsList}>
                        {!isPaymentMethodsLoading && !isPaymentRequestsLoading ? (
                          paymentMethodsToShow.map((item) => {
                            const displayName = item.paymentMethodDetails.displayName
                            const label = item.tokenizedCreditCard
                              ? `${CardBrandNiceTypeMap.get(
                                  item.tokenizedCreditCard.brandId,
                                )} ${displayName}`
                              : `${item.paymentMethodDetails.type} ${displayName}`

                            return (
                              <SelectItem
                                key={item.id}
                                label={label}
                                isActive={item.id === store.selectedPaymentMethod?.id}
                                onClick={() => store.setSelectedPaymentMethod(item)}
                                error={""}
                              />
                            )
                          })
                        ) : (
                          <SelectItem label={"••••"} isActive={true} />
                        )}
                      </div>
                      <DataGrid
                        icon={<CreditCardOutline className={styles.sectionIcon} />}
                        title="Payment info"
                        data={paymentData}
                        onDataReveal={revealSelectedPaymentMethod}
                      />
                    </>
                  )}
                </div>
              </>
            ) : null}
            {allowBooking ? (
              <>
                <h3 className={styles.sectionTitle}>Cabin details</h3>
                <div className={styles.inputRow}>
                  <FormSelectInput
                    label="Deck (optional)"
                    control={control}
                    name="deck"
                    testID="deck-select"
                    maxPopoverHeight={225}
                    options={decksOptions}
                    parentBackgroundColor={colors.backgroundPrimary}
                    defaultValue={store.deckId.toString()}
                    handleChange={(value) => {
                      const foundDeck = decks.find((deck) => deck.id.toString() === value)
                      if (foundDeck) {
                        store.setDeck({
                          id: foundDeck.id,
                          name: foundDeck.name,
                        })
                      }
                    }}
                  />
                  <DefaultInput
                    value={store.cabinNo}
                    onChange={(value) => store.setCabinNo(value)}
                    name="cabinNo"
                    label="Cabin number (optional)"
                    parentBackgroundColor={colors.backgroundPrimary}
                    testId="cabin-number-input"
                  />
                </div>
                <div className={styles.inputRow}>
                  <FormSelectInput
                    name="cabinType"
                    label="Cabin type"
                    options={cabinTypesOptions}
                    control={control}
                    parentBackgroundColor={colors.backgroundSecondary}
                    defaultValue={store.cabinGrade}
                    handleChange={(value) => {
                      store.setCabinGrade(value as CabinGradeType)
                    }}
                    testID="cabin-type-select"
                  />
                  <DefaultInput
                    value={store.confirmationNumber}
                    onChange={(value) => store.setConfirmationNumber(value)}
                    name="confirmationNumber"
                    label="Confirmation number"
                    parentBackgroundColor={colors.backgroundPrimary}
                    testId="confirmation-number-input"
                  />
                </div>
                <div className={classNames(styles.inputRow, styles.lastInputRow)}>
                  <FormCurrencyInput
                    control={control}
                    name="cabinFare"
                    label="Cabin fare"
                    parentBackgroundColor={colors.backgroundPrimary}
                    testId="cabin-fare-input"
                  />
                  <FormCurrencyInput
                    control={control}
                    name="taxesAndFees"
                    label="Taxes and fees"
                    parentBackgroundColor={colors.backgroundPrimary}
                    testId="taxes-and-fees-input"
                  />
                </div>
                <h3 className={styles.sectionTitle}>Upload confirmation</h3>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  data-testid="confirmation-file-input"
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                />
                <button
                  className={styles.upload}
                  onClick={handleAttachFile}
                  data-testid="upload-file-button"
                >
                  {confirmationFile || isDemoTrip ? (
                    <>
                      <FileFill />
                      <p className={styles.uploadText}>
                        {isDemoTrip ? "exampleConfirmation.pdf" : confirmationFile.name}
                      </p>{" "}
                    </>
                  ) : (
                    <>
                      <UploadFill width={24} height={24} className={styles.uploadIcon} />{" "}
                      <p className={styles.uploadText}>
                        Drag & drop or <span className={styles.highlightText}>choose file</span> to
                        upload
                      </p>
                      <p className={styles.uploadFormats}>PDF, JPEG, or PNG</p>
                    </>
                  )}
                </button>
              </>
            ) : null}
          </div>
          {allowBooking || isRoomBooking ? (
            <div className={styles.footer}>
              <button
                className={styles.cancelButton}
                disabled={isRoomBooking}
                type="button"
                onClick={closeModal}
              >
                Cancel
              </button>
              <div className={styles.saveContainer}>
                <div className={styles.total}>
                  <p className={styles.totalPrice}>
                    Total:{" "}
                    <span>
                      {formatCurrency(
                        currencyToNumber(cabinFare) + currencyToNumber(taxesAndFees),
                        2,
                      )}
                    </span>
                  </p>
                  <p className={styles.taxesAndFeesIncluded}>taxes and fees included</p>
                </div>
                <button
                  className={styles.saveButton}
                  disabled={isSaveButtonDisabled || isRoomBooking || hasCruiseSailingDatePassed}
                  type="button"
                  data-testid="book-room-button"
                  onClick={() => {
                    if (isDemoTrip) {
                      return bookRoom(null)
                    }
                    bookRoom({
                      tripRoom: roomData,
                      tripSearchResult: cruiseDetailsData.tripSearchResult,
                      cabinFare,
                      taxesAndFees,
                      cabinGrade: store.cabinGrade,
                      paymentRequestId: store.approvedPaymentRequest.id,
                    })
                  }}
                >
                  {isRoomBooking ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </SkeletonTheme>
    )
  },
)
